/**
 * Wizard de edição de Template WhatsApp WABA
 * Layout premium de 3 colunas: Sidebar + Formulário + Preview
 * 
 * Inclui suporte completo a botões ORDER_DETAILS (PIX)
 * e URLs dinâmicas com variáveis separadas
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { 
  ChevronDown,
  Loader2, 
  AlertTriangle, 
  Settings, 
  MessageSquare,
  FileText,
  Plus,
  Trash2,
  ExternalLink,
  Phone,
  CreditCard,
  QrCode,
  Info,
  Check,
  Layers,
  Hash,
} from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
// Sanitizador centralizado de emojis — Meta rejeita templates com emoji nos textos
import { stripEmojis, useEmojiSafeHandler, containsEmoji } from '@/lib/templates/sanitize-emojis';
import { logger } from '@/lib/logger';
import { cn } from '@/lib/utils';

// Componentes do wizard
import { TemplatePreviewPanel } from './wizard/TemplatePreviewPanel';
import { HeaderFormat, TemplateButton, HEADER_FORMATS, TemplateClassification } from './wizard/types';
import { MediaUrlInput } from '@/components/ui/media-url-input';

// ============================================================================
// INTERFACES
// ============================================================================

interface WhatsAppTemplate {
  id: string;
  inbox_id: string;
  template_id: string;
  name: string;
  language: string;
  category: string;
  status: string;
  rejection_reason: string | null;
  components: any;
  variables: string[] | null;
  example_values: Record<string, string> | null;
  provider_metadata: any;
  created_at: string;
  submitted_at: string | null;
  approved_at: string | null;
  classification_id: string | null;
}

interface EditTemplateWizardProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  template: WhatsAppTemplate;
  onSuccess: () => void;
  tenantId?: string;
}

// ============================================================================
// CONSTANTES
// ============================================================================

const MAX_BUTTONS = 10;
const MAX_BUTTON_TEXT = 25;

// ============================================================================
// HELPERS
// ============================================================================

const getComponent = (components: any[], type: string) => {
  if (!components || !Array.isArray(components)) return null;
  return components.find(c => c.type === type);
};

// ============================================================================
// COMPONENTE PRINCIPAL
// ============================================================================

export function EditTemplateWizard({
  open,
  onOpenChange,
  template,
  onSuccess,
  tenantId,
}: EditTemplateWizardProps) {
  const [loading, setLoading] = useState(false);
  
  // Estados do formulário
  const [headerText, setHeaderText] = useState('');
  const [headerMediaUrl, setHeaderMediaUrl] = useState('');
  const [bodyText, setBodyText] = useState('');
  const [footerText, setFooterText] = useState('');
  const [exampleValues, setExampleValues] = useState<Record<string, string>>({});
  const [buttons, setButtons] = useState<TemplateButton[]>([]);
  const [classifications, setClassifications] = useState<TemplateClassification[]>([]);
  const [classificationId, setClassificationId] = useState<string | null>(null);

  // Templates aprovados têm edição restrita
  const isApproved = template.status === 'APPROVED';
  
  // Detectar formato do header
  const originalHeader = getComponent(template.components || [], 'HEADER');
  const headerFormat: HeaderFormat = originalHeader?.format || 'NONE';

  // --------------------------------------------------------------------------
  // CARREGAR DADOS DO TEMPLATE
  // --------------------------------------------------------------------------
  
  useEffect(() => {
    if (open && template) {
      const components = template.components || [];
      
      const header = getComponent(components, 'HEADER');
      const body = getComponent(components, 'BODY');
      const footer = getComponent(components, 'FOOTER');
      
      setHeaderText(header?.text || '');
      setBodyText(body?.text || '');
      setFooterText(footer?.text || '');
      setExampleValues(template.example_values || {});
      setClassificationId(template.classification_id);
      
      // Buscar header_media_url permanente do banco, com fallback para header_handle
      (async () => {
        const { data: tmplData } = await supabase
          .from('whatsapp_templates')
          .select('header_media_url')
          .eq('id', template.id)
          .single();
        
        const permanentUrl = (tmplData as any)?.header_media_url;
        const fallbackUrl = header?.example?.header_handle?.[0];
        setHeaderMediaUrl(permanentUrl || fallbackUrl || '');
      })();
      
      fetchClassifications();
      
      // Carregar botões existentes
      const buttonsComponent = getComponent(components, 'BUTTONS');
      if (buttonsComponent?.buttons) {
        setButtons(buttonsComponent.buttons.map((btn: any) => {
          if (btn.type === 'ORDER_DETAILS') {
            const pixSettings = btn.order_details?.payment_settings?.[0]?.pix_dynamic_code;
            return {
              type: btn.type,
              text: btn.text || '',
              pix_code: pixSettings?.code || '',
              merchant_name: pixSettings?.merchant_name || '',
              pix_key: pixSettings?.key || '',
              pix_key_type: pixSettings?.key_type || 'CNPJ',
              amount_value: (btn.order_details?.total_amount?.value || 0) / 100,
              item_name: btn.order_details?.order?.items?.[0]?.name || '',
            };
          }
          
          const isDynamic = btn.url?.endsWith('{{1}}');
          const exampleValue = btn.example?.[0] || '';
          
          return {
            type: btn.type,
            text: btn.text || '',
            url: isDynamic ? btn.url.replace(/\{\{1\}\}$/, '') : btn.url,
            url_type: isDynamic ? 'dynamic' : 'static',
            phone_number: btn.phone_number,
            example_value: exampleValue,
          };
        }));
      } else {
        setButtons([]);
      }
    }
  }, [open, template]);

  const fetchClassifications = async () => {
    try {
      const { data: inboxData } = await supabase
        .from('inboxes')
        .select('tenant_id')
        .eq('id', template.inbox_id)
        .single();
      
      if (!inboxData?.tenant_id) return;
      
      const { data, error } = await supabase
        .from('template_classifications')
        .select('id, name, color')
        .eq('tenant_id', inboxData.tenant_id)
        .order('name');

      if (error) throw error;
      setClassifications(data || []);
    } catch (error) {
      logger.error('[EditTemplateWizard] Erro ao buscar classificações:', error);
    }
  };

  // --------------------------------------------------------------------------
  // VARIÁVEIS
  // --------------------------------------------------------------------------
  
  const extractBodyVariables = (): string[] => {
    const matches = bodyText.match(/\{\{(\d+)\}\}/g);
    if (!matches) return [];
    return [...new Set(matches)].sort();
  };

  // ---------------------------------------------------------------------------
  // SANITIZAÇÃO DE EMOJIS — Meta rejeita templates com emoji nos textos
  // de definição (header/body/footer/botões).
  // ---------------------------------------------------------------------------
  const handleHeaderTextChange = useEmojiSafeHandler(setHeaderText);
  // Corpo permite emojis (Meta aceita emojis no BODY) — setter direto
  const handleBodyTextChange = (value: string) => setBodyText(value);
  const handleFooterTextChange = useEmojiSafeHandler(setFooterText);

  // Conjunto de campos do botão que aceitam texto livre e devem ser
  // sanitizados. Campos técnicos (phone_number, pix_code, pix_key,
  // amount_value, url, *_type) ficam de fora.
  const EMOJI_SANITIZED_BUTTON_FIELDS = new Set<keyof TemplateButton>([
    'text',
    'example_value',
    'code_example',
    'merchant_name',
    'item_name',
  ]);
  const lastEmojiToastAtRef = useRef<number>(0);
  const showEmojiToastDebounced = () => {
    const now = Date.now();
    if (now - lastEmojiToastAtRef.current > 1500) {
      lastEmojiToastAtRef.current = now;
      toast.warning(
        'Emojis não são permitidos em templates do WhatsApp Oficial. O caractere foi removido.'
      );
    }
  };

  const updateExampleValue = (variable: string, value: string) => {
    // Sanitiza emoji em valores de exemplo (Meta também rejeita)
    const { clean, removed } = stripEmojis(value);
    if (removed) showEmojiToastDebounced();
    setExampleValues(prev => ({
      ...prev,
      [variable.replace(/[{}]/g, '')]: clean,
    }));
  };

  // --------------------------------------------------------------------------
  // GERENCIAMENTO DE BOTÕES
  // --------------------------------------------------------------------------
  
  const addButton = (type: 'URL' | 'PHONE_NUMBER' | 'QUICK_REPLY' | 'ORDER_DETAILS') => {
    if (buttons.length >= MAX_BUTTONS) {
      toast.error(`Máximo de ${MAX_BUTTONS} botões permitidos`);
      return;
    }
    
    const newButton: TemplateButton = {
      type,
      text: type === 'ORDER_DETAILS' ? 'Review and Pay' : '',
      url_type: 'static',
      example_value: '',
      pix_key_type: type === 'ORDER_DETAILS' ? 'CNPJ' : undefined,
    };
    
    setButtons(prev => [...prev, newButton]);
  };

  const removeButton = (index: number) => {
    setButtons(prev => prev.filter((_, i) => i !== index));
  };

  const updateButton = (index: number, field: keyof TemplateButton, value: any) => {
    let nextValue = value;
    // Se for campo de texto livre, remove emojis automaticamente
    if (typeof value === 'string' && EMOJI_SANITIZED_BUTTON_FIELDS.has(field)) {
      const { clean, removed } = stripEmojis(value);
      nextValue = clean;
      if (removed) showEmojiToastDebounced();
    }
    setButtons(prev => prev.map((btn, i) => {
      if (i !== index) return btn;
      return { ...btn, [field]: nextValue };
    }));
  };

  // --------------------------------------------------------------------------
  // BUILD COMPONENTS
  // --------------------------------------------------------------------------
  
  const buildComponents = () => {
    const components: any[] = [];
    const originalComponents = template.components || [];

    // Header (mantém formato original)
    if (originalHeader) {
      const header = { ...originalHeader };
      if (header.format === 'TEXT') {
        header.text = headerText;
      }
      components.push(header);
    }

    // Body
    components.push({
      type: 'BODY',
      text: bodyText,
    });

    // Footer
    if (footerText.trim()) {
      components.push({
        type: 'FOOTER',
        text: footerText,
      });
    }

    // Buttons
    if (buttons.length > 0) {
      components.push({
        type: 'BUTTONS',
        buttons: buttons.map(btn => {
          const buttonObj: any = {
            type: btn.type,
            text: btn.text,
          };
          
          if (btn.type === 'URL' && btn.url) {
            buttonObj.url = btn.url_type === 'dynamic' 
              ? `${btn.url}{{1}}` 
              : btn.url;
            
            if (btn.url_type === 'dynamic' && btn.example_value) {
              buttonObj.example = [btn.example_value];
            }
          }
          
          if (btn.type === 'PHONE_NUMBER' && btn.phone_number) {
            buttonObj.phone_number = btn.phone_number;
          }
          
          if (btn.type === 'ORDER_DETAILS') {
            buttonObj.order_details = {
              reference_id: crypto.randomUUID(),
              type: 'digital-goods',
              payment_type: 'br',
              payment_settings: [{
                type: 'pix_dynamic_code',
                pix_dynamic_code: {
                  code: btn.pix_code || '',
                  merchant_name: btn.merchant_name || '',
                  key: btn.pix_key || '',
                  key_type: btn.pix_key_type || 'CNPJ',
                }
              }],
              currency: 'BRL',
              total_amount: {
                value: Math.round((btn.amount_value || 0) * 100),
                offset: 100
              },
              order: {
                items: [{ name: btn.item_name || 'Pagamento' }]
              }
            };
          }
          
          return buttonObj;
        }),
      });
    }

    return components;
  };

  // --------------------------------------------------------------------------
  // VALIDAÇÕES
  // --------------------------------------------------------------------------
  
  const validateButtons = (): boolean => {
    for (let i = 0; i < buttons.length; i++) {
      const btn = buttons[i];
      
      if (!btn.text.trim()) {
        toast.error(`Botão ${i + 1}: Texto é obrigatório`);
        return false;
      }
      
      if (btn.text.length > MAX_BUTTON_TEXT) {
        toast.error(`Botão ${i + 1}: Texto deve ter no máximo ${MAX_BUTTON_TEXT} caracteres`);
        return false;
      }
      
      if (btn.type === 'URL' && !btn.url?.trim()) {
        toast.error(`Botão ${i + 1}: URL é obrigatória`);
        return false;
      }
      
      if (btn.type === 'PHONE_NUMBER' && !btn.phone_number?.trim()) {
        toast.error(`Botão ${i + 1}: Número de telefone é obrigatório`);
        return false;
      }
      
      if (btn.type === 'ORDER_DETAILS') {
        if (!btn.pix_code?.trim()) {
          toast.error(`Botão ${i + 1}: Código PIX é obrigatório`);
          return false;
        }
        if (!btn.merchant_name?.trim()) {
          toast.error(`Botão ${i + 1}: Nome do recebedor é obrigatório`);
          return false;
        }
        if (!btn.pix_key?.trim()) {
          toast.error(`Botão ${i + 1}: Chave PIX é obrigatória`);
          return false;
        }
        if (!btn.amount_value || btn.amount_value <= 0) {
          toast.error(`Botão ${i + 1}: Valor do pagamento deve ser maior que zero`);
          return false;
        }
      }
    }
    
    return true;
  };

  // --------------------------------------------------------------------------
  // SUBMIT
  // --------------------------------------------------------------------------
  
  const handleSubmit = async () => {
    if (!bodyText.trim()) {
      toast.error('Corpo da mensagem é obrigatório');
      return;
    }

    if (!validateButtons()) {
      return;
    }

    // Validação defensiva contra emojis (defesa em profundidade)
    const emojiFields: string[] = [];
    if (containsEmoji(headerText)) emojiFields.push('Cabeçalho');
    // Corpo permite emojis (Meta aceita emojis no BODY de templates)
    if (containsEmoji(footerText)) emojiFields.push('Rodapé');
    Object.values(exampleValues || {}).forEach((v) => {
      if (containsEmoji(v) && !emojiFields.includes('Valores de exemplo')) {
        emojiFields.push('Valores de exemplo');
      }
    });
    buttons.forEach((btn, idx) => {
      const buttonHasEmoji =
        containsEmoji(btn.text) ||
        containsEmoji(btn.example_value) ||
        containsEmoji(btn.code_example) ||
        containsEmoji(btn.merchant_name) ||
        containsEmoji(btn.item_name);
      if (buttonHasEmoji) emojiFields.push(`Botão ${idx + 1}`);
    });
    if (emojiFields.length > 0) {
      toast.error(
        `Remova os emojis antes de salvar. Campos com emoji: ${emojiFields.join(', ')}`
      );
      return;
    }

    try {
      setLoading(true);

      const components = buildComponents();
      const bodyVariables = extractBodyVariables();

      // Buscar provider da inbox do template
      const { data: inboxData } = await supabase
        .from('inboxes')
        .select(`
          id,
          api_providers (
            slug
          )
        `)
        .eq('id', template.inbox_id)
        .single();

      const providerSlug = (inboxData?.api_providers as any)?.slug;

      logger.info('[EditTemplateWizard]', `Atualizando template via provider: ${providerSlug || 'local'}`);

      // Se for meta-whatsapp, atualiza diretamente na Meta via Edge Function
      if (providerSlug === 'meta-whatsapp') {
        const { data: sessionData } = await supabase.auth.getSession();
        if (!sessionData?.session?.access_token) {
          toast.error('Sessão expirada. Faça login novamente.');
          return;
        }

        const { data, error } = await supabase.functions.invoke('manage-template', {
          body: {
            inbox_id: template.inbox_id,
            action: 'update',
            template_name: template.name,
            template_data: {
              name: template.name,
              category: template.category,
              language: template.language,
              components,
            },
          },
        });

        if (error) {
          logger.error('[EditTemplateWizard] Erro ao atualizar template na Meta:', error);
          toast.error(error.message || 'Erro ao atualizar template na Meta');
          return;
        }

        if (!data?.success) {
          if (data?.error?.includes('cannot be updated')) {
            logger.warn('[EditTemplateWizard]', 'Template não pode ser modificado na Meta, salvando localmente');
          } else {
            toast.error(data?.error || 'Erro ao atualizar template na Meta');
            return;
          }
        } else {
          toast.success('Template atualizado na Meta!');
          logger.info('[EditTemplateWizard]', `Template ${template.name} atualizado na Meta`);
        }
      }

      // Atualiza no banco local (inclui header_media_url permanente)
      const updateData: any = {
        components,
        variables: bodyVariables.length > 0 ? bodyVariables : null,
        example_values: Object.keys(exampleValues).length > 0 ? exampleValues : null,
        classification_id: classificationId,
        header_media_url: headerMediaUrl?.startsWith('http') ? headerMediaUrl : null,
      };

      if (template.status === 'REJECTED') {
        updateData.status = 'PENDING';
        updateData.rejection_reason = null;
      }

      const { error: dbError } = await supabase
        .from('whatsapp_templates')
        .update(updateData)
        .eq('id', template.id);

      if (dbError) throw dbError;

      if (providerSlug !== 'meta-whatsapp') {
        toast.success('Template atualizado!');
      }
      
      logger.info('[EditTemplateWizard]', `Template ${template.name} atualizado no banco`);
      onSuccess();
    } catch (error) {
      logger.error('[EditTemplateWizard] Erro ao atualizar:', error);
      toast.error('Erro ao atualizar template');
    } finally {
      setLoading(false);
    }
  };

  const bodyVariables = extractBodyVariables();

  // Resumo para a sidebar
  const summary = {
    category: template.category,
    language: template.language,
    buttonCount: buttons.length,
    hasHeader: headerFormat !== 'NONE',
  };

  // --------------------------------------------------------------------------
  // ÍCONE DE BOTÃO
  // --------------------------------------------------------------------------
  
  const ButtonIcon = ({ type }: { type: string }) => {
    switch (type) {
      case 'URL':
        return <ExternalLink className="h-4 w-4 text-muted-foreground" />;
      case 'PHONE_NUMBER':
        return <Phone className="h-4 w-4 text-muted-foreground" />;
      case 'QUICK_REPLY':
        return <MessageSquare className="h-4 w-4 text-muted-foreground" />;
      case 'ORDER_DETAILS':
        return <CreditCard className="h-4 w-4 text-muted-foreground" />;
      default:
        return null;
    }
  };

  // --------------------------------------------------------------------------
  // RENDER
  // --------------------------------------------------------------------------

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent 
        className="max-w-6xl max-h-[90vh] h-[90vh] p-0 gap-0 flex flex-col"
        hideCloseButton
        preventCloseOnOutsideInteraction
      >
        <div className="grid grid-cols-1 md:grid-cols-[280px_1fr] lg:grid-cols-[280px_1fr_320px] h-full min-h-0 flex-1">
          
          {/* ===== SIDEBAR ===== */}
          <aside 
            className="hidden md:flex flex-col relative overflow-hidden"
            style={{
              background: 'linear-gradient(135deg, hsl(210, 80%, 15%) 0%, hsl(210, 70%, 20%) 50%, hsl(200, 60%, 25%) 100%)'
            }}
          >
            {/* Elementos decorativos */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
              <div className="absolute -top-20 -right-20 w-40 h-40 bg-white/5 rounded-full blur-3xl" />
              <div className="absolute top-1/2 -left-20 w-60 h-60 bg-white/5 rounded-full blur-3xl" />
              <div className="absolute -bottom-10 right-4 w-32 h-32 bg-white/5 rounded-full blur-2xl" />
            </div>
            
            <div className="relative z-10 flex flex-col h-full p-5">
              {/* Header da sidebar */}
              <div className="mb-6">
                <div className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center mb-4">
                  <FileText className="h-6 w-6 text-white" />
                </div>
                <h2 className="text-lg font-semibold text-white">Editar Modelo</h2>
                <p className="text-sm text-white/60 mt-1 truncate">
                  {template.name}
                </p>
              </div>
              
              {/* Info do template */}
              <div className="space-y-3 mb-6">
                <div className="flex items-center gap-3 px-3 py-3 rounded-lg bg-white/10 border border-white/20">
                  <div className="w-8 h-8 rounded-full flex items-center justify-center bg-white">
                    <MessageSquare className="h-4 w-4 text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <span className="block text-sm font-medium text-white">Mensagem</span>
                    <span className="block text-xs text-white/40">Header, corpo, rodapé e botões</span>
                  </div>
                </div>
              </div>
              
              {/* Resumo */}
              <div className="mt-auto pt-4 border-t border-white/10">
                <h3 className="text-xs font-medium text-white/60 uppercase tracking-wider mb-3">
                  Resumo
                </h3>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Layers className="h-4 w-4 text-white/60" />
                      <span className="text-sm text-white/80">Categoria</span>
                    </div>
                    <span className="text-sm font-medium text-white">
                      {summary.category}
                    </span>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Hash className="h-4 w-4 text-white/60" />
                      <span className="text-sm text-white/80">Botões</span>
                    </div>
                    <span className="text-sm font-medium text-white">
                      {summary.buttonCount}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </aside>
          
          {/* ===== CONTEÚDO PRINCIPAL ===== */}
          <main className="flex flex-col h-full min-h-0">
            
            {/* Header */}
            <DialogHeader className="flex-shrink-0 px-6 py-5 border-b bg-background">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                  <MessageSquare className="h-6 w-6 text-primary" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <DialogTitle className="text-lg font-semibold">
                      {template.name}
                    </DialogTitle>
                    <Badge variant="outline" className={cn(
                      "text-xs",
                      template.status === 'APPROVED' && "bg-green-500/10 text-green-600",
                      template.status === 'PENDING' && "bg-amber-500/10 text-amber-600",
                      template.status === 'REJECTED' && "bg-destructive/10 text-destructive",
                    )}>
                      {template.status}
                    </Badge>
                  </div>
                  <DialogDescription className="text-sm">
                    {template.category} • {template.language}
                  </DialogDescription>
                </div>
              </div>
            </DialogHeader>
            
            {/* Área scrollável */}
            <div className="flex-1 overflow-y-auto min-h-0 p-6 space-y-6">
              
              {/* Aviso para templates aprovados */}
              {isApproved && (
                <div className="flex items-start gap-3 p-3 rounded-lg bg-amber-500/10 text-amber-600 text-sm">
                  <AlertTriangle className="h-5 w-5 flex-shrink-0 mt-0.5" />
                  <div>
                    <strong>Template Aprovado:</strong> Alterações no conteúdo requerem 
                    nova aprovação da Meta.
                  </div>
                </div>
              )}

              {/* Motivo de rejeição */}
              {template.status === 'REJECTED' && template.rejection_reason && (
                <div className="p-3 rounded-lg bg-destructive/10 text-destructive text-sm">
                  <strong>Motivo da Rejeição:</strong> {template.rejection_reason}
                </div>
              )}

              {/* Classificação */}
              <div className="space-y-2">
                <Label>Classificação</Label>
                <Select 
                  value={classificationId || 'none'} 
                  onValueChange={(v) => setClassificationId(v === 'none' ? null : v)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione uma classificação" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Sem classificação</SelectItem>
                    {classifications.map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        <div className="flex items-center gap-2">
                          <div 
                            className="w-3 h-3 rounded-full" 
                            style={{ backgroundColor: c.color }} 
                          />
                          {c.name}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Header (se existir e for texto) */}
              {headerFormat === 'TEXT' && (
                <div className="space-y-2">
                  <Label>Header</Label>
                  <Input
                    value={headerText}
                    onChange={(e) => handleHeaderTextChange(e.target.value)}
                    placeholder="Texto do header"
                    maxLength={60}
                    disabled={isApproved}
                  />
                </div>
              )}

              {/* Header de mídia (IMAGE, VIDEO, DOCUMENT) */}
              {['IMAGE', 'VIDEO', 'DOCUMENT'].includes(headerFormat) && (
                <div className="space-y-2">
                  <Label>Mídia do Header ({headerFormat === 'IMAGE' ? 'Imagem' : headerFormat === 'VIDEO' ? 'Vídeo' : 'Documento'})</Label>
                  <MediaUrlInput
                    value={headerMediaUrl || null}
                    onChange={(url) => setHeaderMediaUrl(url || '')}
                    showPreview={headerFormat === 'IMAGE'}
                    allowedTypes={
                      headerFormat === 'IMAGE' ? ['image'] :
                      headerFormat === 'VIDEO' ? ['video'] :
                      ['document']
                    }
                  />
                </div>
              )}

              {/* Body */}
              <div className="space-y-2">
                <Label>Corpo da Mensagem *</Label>
                <Textarea
                  value={bodyText}
                  onChange={(e) => handleBodyTextChange(e.target.value)}
                  rows={4}
                  maxLength={1024}
                  disabled={isApproved}
                />
                <p className="text-[10px] text-muted-foreground">
                  Use {`{{1}}`}, {`{{2}}`}, etc. para variáveis dinâmicas
                </p>
              </div>

              {/* Footer */}
              <div className="space-y-2">
                <Label>Rodapé</Label>
                <Input
                  value={footerText}
                  onChange={(e) => handleFooterTextChange(e.target.value)}
                  placeholder="Texto do rodapé"
                  maxLength={60}
                  disabled={isApproved}
                />
              </div>

              {/* Editor de Botões */}
              <div className="space-y-3 p-4 border rounded-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-base">Botões</Label>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      Adicione botões para ações rápidas
                    </p>
                  </div>
                  
                  {!isApproved && buttons.length < MAX_BUTTONS && (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="outline" size="sm" className="gap-1">
                          <Plus className="h-4 w-4" />
                          Adicionar
                          <ChevronDown className="h-3 w-3" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => addButton('URL')}>
                          <ExternalLink className="h-4 w-4 mr-2" />
                          Acessar site
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => addButton('PHONE_NUMBER')}>
                          <Phone className="h-4 w-4 mr-2" />
                          Ligar
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => addButton('QUICK_REPLY')}>
                          <MessageSquare className="h-4 w-4 mr-2" />
                          Resposta rápida
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => addButton('ORDER_DETAILS')}>
                          <CreditCard className="h-4 w-4 mr-2" />
                          Pagamento PIX
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                </div>

                {buttons.length === 0 && (
                  <div className="text-center py-4 text-sm text-muted-foreground">
                    Nenhum botão adicionado
                  </div>
                )}

                {buttons.length > 0 && (
                  <div className="space-y-3 mt-4">
                    {buttons.map((btn, index) => (
                      <div 
                        key={index} 
                        className="grid gap-3 p-3 bg-muted/50 rounded-lg border"
                      >
                        {/* Linha 1: Tipo + Texto + Remover */}
                        <div className="grid grid-cols-[140px_1fr_32px] gap-2 items-center">
                          <Select
                            value={btn.type}
                            onValueChange={(value) => updateButton(index, 'type', value)}
                            disabled={isApproved}
                          >
                            <SelectTrigger className="h-9">
                              <div className="flex items-center gap-2">
                                <ButtonIcon type={btn.type} />
                                <SelectValue />
                              </div>
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="URL">Acessar site</SelectItem>
                              <SelectItem value="PHONE_NUMBER">Ligar</SelectItem>
                              <SelectItem value="QUICK_REPLY">Resposta rápida</SelectItem>
                              <SelectItem value="ORDER_DETAILS">Pagamento PIX</SelectItem>
                            </SelectContent>
                          </Select>

                          <Input
                            value={btn.text}
                            onChange={(e) => updateButton(index, 'text', e.target.value)}
                            placeholder="Texto do botão"
                            maxLength={MAX_BUTTON_TEXT}
                            className="h-9"
                            disabled={isApproved}
                          />

                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-9 w-9"
                            onClick={() => removeButton(index)}
                            disabled={isApproved}
                          >
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </div>

                        {/* Campos específicos por tipo */}
                        {btn.type === 'URL' && (
                          <div className="space-y-2">
                            <div className="grid grid-cols-[140px_1fr] gap-2">
                              <Select
                                value={btn.url_type || 'static'}
                                onValueChange={(value) => updateButton(index, 'url_type', value)}
                                disabled={isApproved}
                              >
                                <SelectTrigger className="h-9">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="static">Estática</SelectItem>
                                  <SelectItem value="dynamic">Dinâmica</SelectItem>
                                </SelectContent>
                              </Select>

                              <div className="flex items-center gap-1">
                                <Input
                                  value={btn.url || ''}
                                  onChange={(e) => updateButton(index, 'url', e.target.value)}
                                  placeholder="https://exemplo.com"
                                  className="h-9"
                                  disabled={isApproved}
                                />
                                {btn.url_type === 'dynamic' && (
                                  <Badge variant="secondary" className="shrink-0 text-[10px] px-1.5 font-mono">
                                    {'{{1}}'}
                                  </Badge>
                                )}
                              </div>
                            </div>
                            
                            {btn.url_type === 'dynamic' && (
                              <div className="ml-[148px] space-y-1">
                                <div className="flex items-center gap-2">
                                  <Label className="text-xs text-muted-foreground">Exemplo:</Label>
                                  <Input
                                    value={btn.example_value || ''}
                                    onChange={(e) => updateButton(index, 'example_value', e.target.value)}
                                    placeholder="ex: abc123"
                                    className="h-8 text-sm flex-1"
                                    disabled={isApproved}
                                  />
                                </div>
                              </div>
                            )}
                          </div>
                        )}

                        {btn.type === 'PHONE_NUMBER' && (
                          <div className="grid grid-cols-[140px_1fr] gap-2">
                            <span className="text-xs text-muted-foreground flex items-center">
                              Formato E.164
                            </span>
                            <Input
                              value={btn.phone_number || ''}
                              onChange={(e) => updateButton(index, 'phone_number', e.target.value)}
                              placeholder="+5511999999999"
                              className="h-9"
                              disabled={isApproved}
                            />
                          </div>
                        )}

                        {btn.type === 'ORDER_DETAILS' && (
                          <div className="space-y-3 mt-2">
                            <div className="flex items-center gap-2 p-2 rounded-md bg-green-500/10">
                              <QrCode className="h-4 w-4 text-green-600" />
                              <span className="text-xs text-green-700 dark:text-green-400">
                                Configure os dados do pagamento PIX
                              </span>
                            </div>

                            <div className="space-y-1">
                              <Label className="text-xs">Código PIX Copia-Cola *</Label>
                              <Textarea
                                value={btn.pix_code || ''}
                                onChange={(e) => updateButton(index, 'pix_code', e.target.value)}
                                placeholder="00020126580014br.gov.bcb.pix0136..."
                                rows={3}
                                className="font-mono text-xs"
                                disabled={isApproved}
                              />
                            </div>

                            <div className="space-y-1">
                              <Label className="text-xs">Nome do Recebedor *</Label>
                              <Input
                                value={btn.merchant_name || ''}
                                onChange={(e) => updateButton(index, 'merchant_name', e.target.value)}
                                placeholder="Nome da empresa"
                                className="h-9"
                                disabled={isApproved}
                              />
                            </div>

                            <div className="grid grid-cols-2 gap-2">
                              <div className="space-y-1">
                                <Label className="text-xs">Chave PIX *</Label>
                                <Input
                                  value={btn.pix_key || ''}
                                  onChange={(e) => updateButton(index, 'pix_key', e.target.value)}
                                  placeholder="12.345.678/0001-90"
                                  className="h-9"
                                  disabled={isApproved}
                                />
                              </div>
                              <div className="space-y-1">
                                <Label className="text-xs">Tipo de Chave *</Label>
                                <Select
                                  value={btn.pix_key_type || 'CNPJ'}
                                  onValueChange={(value) => updateButton(index, 'pix_key_type', value)}
                                  disabled={isApproved}
                                >
                                  <SelectTrigger className="h-9">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="CNPJ">CNPJ</SelectItem>
                                    <SelectItem value="CPF">CPF</SelectItem>
                                    <SelectItem value="EMAIL">E-mail</SelectItem>
                                    <SelectItem value="CELULAR">Celular</SelectItem>
                                    <SelectItem value="ALEATORIA">Chave Aleatória</SelectItem>
                                  </SelectContent>
                                </Select>
                              </div>
                            </div>

                            <div className="grid grid-cols-2 gap-2">
                              <div className="space-y-1">
                                <Label className="text-xs">Valor (R$) *</Label>
                                <Input
                                  type="number"
                                  step="0.01"
                                  min="0.01"
                                  value={btn.amount_value || ''}
                                  onChange={(e) => updateButton(index, 'amount_value', parseFloat(e.target.value) || 0)}
                                  placeholder="99.90"
                                  className="h-9"
                                  disabled={isApproved}
                                />
                              </div>
                              <div className="space-y-1">
                                <Label className="text-xs">Descrição</Label>
                                <Input
                                  value={btn.item_name || ''}
                                  onChange={(e) => updateButton(index, 'item_name', e.target.value)}
                                  placeholder="Mensalidade"
                                  className="h-9"
                                  disabled={isApproved}
                                />
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Valores de exemplo do BODY */}
              {bodyVariables.length > 0 && (
                <div className="space-y-3 p-4 border rounded-lg">
                  <div>
                    <Label className="text-base">Valores de Exemplo</Label>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      Exemplos para as variáveis do corpo da mensagem
                    </p>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    {bodyVariables.map((variable) => {
                      const key = variable.replace(/[{}]/g, '');
                      return (
                        <div key={variable} className="space-y-1">
                          <Label className="text-xs font-mono">{variable}</Label>
                          <Input
                            value={exampleValues[key] || ''}
                            onChange={(e) => updateExampleValue(variable, e.target.value)}
                            placeholder={`Ex: valor para ${variable}`}
                          />
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
            
            {/* Footer */}
            <div className="flex-shrink-0 bg-background px-6 py-4 border-t flex justify-between">
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                Cancelar
              </Button>
              <Button onClick={handleSubmit} disabled={loading}>
                {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Salvar Alterações
              </Button>
            </div>
          </main>
          
          {/* ===== PREVIEW ===== */}
          <TemplatePreviewPanel
            headerFormat={headerFormat}
            headerText={headerText}
            headerMediaUrl={headerMediaUrl || undefined}
            bodyText={bodyText}
            footerText={footerText}
            buttons={buttons}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
}
